import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { useCompanies } from "../../../../context/CompanyContext";
import { validateProduct } from "../../../../validation/productValidation";
import { PRODUCT_STATUS } from "../../../../constants/productConstants";
import classes from "./EditProductModal.module.scss";
import { getUnassignedBases } from "../../../../api/baseApi";

// Product type mappings for auto-fill
const PRODUCT_TYPE_MAPPINGS = {
  3: { probe_type: "Flexible", probelength: 13, probesensnum: 6 },
  4: { probe_type: "Flexible", probelength: 10, probesensnum: 6 },
  5: { probe_type: "Fix", probelength: 4, probesensnum: 4 },
  6: { probe_type: "Fix", probelength: 4, probesensnum: 3 },
};

const EditProductModal = ({
  t,
  show,
  onClose,
  onSave,
  product,
  productTypes = [],
  tableType = "products",
}) => {
  const { companies } = useCompanies();
  const [formData, setFormData] = useState({
    baseid: "",
    probeid: "",
    regnumber: "",
    probelength: "",
    probesensnum: "",
    imeei: "",
    sim: "",
    comments: "",
    status: "T",
    companies_id: "",
    sim_id: "",
    probe_type: "",
    product_types_id: "",
    products_id: "",
  });
  const [validated, setValidated] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState("");
  const [unassignedBases, setUnassignedBases] = useState([]);
  const [selectedBase, setSelectedBase] = useState("");

  // Initialize form data when product changes
  useEffect(() => {
    if (product) {
      setFormData({
        baseid: product.baseid || "",
        probeid: product.probeid?.toString() || "",
        regnumber: product.regnumber || "",
        probelength: product.probelength?.toString() || "",
        probesensnum: product.probesensnum?.toString() || "",
        imeei: product.imeei || "",
        sim: product.sim || "",
        comments: product.comments || "",
        status: product.status || "T",
        companies_id: product.companies_id?.toString() || "",
        sim_id: product.sim_id?.toString() || "",
        probe_type: product.probe_type || "",
        product_types_id: product.product_types_id?.toString() || "",
      });
    }
  }, [product]);

  // Helper functions to determine product type
  const isBaseProduct = () => {
    const productTypeId = parseInt(formData.product_types_id, 10);
    return productTypeId === 1 || productTypeId === 2;
  };

  const isProbeProduct = () => {
    const productTypeId = parseInt(formData.product_types_id, 10);
    return [3, 4, 5, 6].includes(productTypeId);
  };

  // Function to fetch unassigned bases
  const fetchUnassignedBases = async () => {
    try {
      const result = await getUnassignedBases();
      // Sort unassigned bases by baseid
      result.sort((a, b) => {
        // Extract any number sequences from the baseids
        const aMatches = a.baseid.match(/(\d+)/g);
        const bMatches = b.baseid.match(/(\d+)/g);
        if (aMatches && bMatches) {
          // If both have numbers, compare the first number in each
          const aNum = parseInt(aMatches[0]);
          const bNum = parseInt(bMatches[0]);
          if (aNum !== bNum) {
            return aNum - bNum; // Numeric comparison
          }
        }
        // Fall back to string comparison if numbers are equal or not present
        return a.baseid.localeCompare(b.baseid);
      });
      // Set the unassigned bases in state
      setUnassignedBases(result);
    } catch (error) {
      console.error("Error fetching unassigned bases:", error);
      setError(t("error_fetching_unassigned_bases"));
    }
  };

  // Fetch unassigned bases when modal opens
  useEffect(() => {
    if (show) {
      fetchUnassignedBases();
    }
  }, [show]);

  // Handle product type change
  const handleProductTypeChange = (e) => {
    const { value } = e.target;
    const numericValue = parseInt(value, 10); // Convert string to number
    if (isNaN(numericValue)) {
      console.error("Invalid product type ID selected:", value);
      setFormData((prev) => ({
        ...prev,
        product_types_id: "", // Reset if invalid
      }));
      return;
    }
    // Update the product type ID in form data
    setFormData((prev) => ({
      ...prev,
      product_types_id: value, // Keep as string for form state
    }));

    // if the selected product type is a base automatically fill with "Base"
    if (numericValue === 1 || numericValue === 2) {
      setFormData((prev) => ({
        ...prev,
        probe_type: "Base",
        probelength: "",
        probesensnum: "",
      }));
      return; // No need to auto-fill probe details for base products
    }
    // If we have mapping data for this product type, auto-fill the fields
    if (value && PRODUCT_TYPE_MAPPINGS[numericValue]) {
      const mapping = PRODUCT_TYPE_MAPPINGS[numericValue];

      setFormData((prev) => ({
        ...prev,
        probe_type: mapping.probe_type.trim(),
        probelength: mapping.probelength.toString(),
        probesensnum: mapping.probesensnum.toString(),
      }));
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Clone formData to avoid modifying state directly
    const formDataToSubmit = { ...formData };

    // Safely trim probe_type to ensure no leading/trailing spaces
    if (formDataToSubmit.probe_type) {
      formDataToSubmit.probe_type = formDataToSubmit.probe_type.trim();
    }
    // Validate form data
    const errors = validateProduct(formDataToSubmit);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError(t("please_fix_validation_errors"));
      return;
    }

    // Clear validation errors when form is valid
    setValidationErrors({});
    setError("");

    // Convert numeric fields to numbers
    formDataToSubmit.probeid = parseInt(formDataToSubmit.probeid, 10) || null;
    formDataToSubmit.probelength =
      parseInt(formDataToSubmit.probelength, 10) || null;
    formDataToSubmit.probesensnum =
      parseInt(formDataToSubmit.probesensnum, 10) || null;
    formDataToSubmit.companies_id = parseInt(formDataToSubmit.companies_id, 10);
    formDataToSubmit.product_types_id = parseInt(
      formDataToSubmit.product_types_id,
      10
    );
    formDataToSubmit.sim_id = parseInt(formDataToSubmit.sim_id, 10) || null;

    // Convert empty strings to null for specific fields
    if (isProbeProduct()) {
      // For probe products, baseid should be null, not empty string
      formDataToSubmit.baseid = null;
      formDataToSubmit.imeei = null;
      formDataToSubmit.sim = null;
      formDataToSubmit.sim_id = null;
      if (formDataToSubmit.comments.length === 0) {
        formDataToSubmit.comments = null; // Set comments to null if empty
      }
    }

    if (isBaseProduct()) {
      // For base products, probeid should be null, not empty string
      formDataToSubmit.probeid = null;
      formDataToSubmit.probelength = null;
      formDataToSubmit.probesensnum = null;
      if (formDataToSubmit.comments.length === 0) {
        formDataToSubmit.comments = null; // Set comments to null if empty
      }
    }

    // Add the original product ID for update
    formDataToSubmit.id = product.id;

    try {
      onSave(formDataToSubmit);
    } catch (err) {
      setError(t("error_updating_product"));
    }
  };

  // Handle base selection
  const handleBaseChange = (e) => {
    const baseid = e.target.value;
    setSelectedBase(baseid);

    if (!baseid) {
      // If no base is selected, clear the baseid field
      setFormData((prev) => ({
        ...prev,
        baseid: "",
        sim_id: "",
        imeei: "",
        sim: "",
      }));
      return;
    }
    // Find the selected base and update form data
    const selectedBase = unassignedBases.find((base) => base.baseid === baseid);
    if (selectedBase) {
      setFormData((prev) => ({
        ...prev,
        baseid: selectedBase.baseid || "",
        sim_id: selectedBase.sim_id || "",
        imeei: selectedBase.imeei || "",
        sim: selectedBase.sim || "",
      }));
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      className={classes.editProductModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("edit_product")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row>
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Label>{t("product_type")}</Form.Label>
              <Form.Control
                as="select"
                name="product_types_id"
                value={formData.product_types_id}
                onChange={handleProductTypeChange}
                required
                isInvalid={!!validationErrors.product_types_id}
                className={`${
                  tableType ? `modal-${tableType}-field-product_types_id` : ""
                }`}
              >
                <option value="">{t("choose")}</option>
                {productTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.product_longname}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {validationErrors.product_types_id
                  ? t(validationErrors.product_types_id)
                  : t("required_field")}
              </Form.Control.Feedback>
            </Col>

            {/* Registration Number */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="regnumber">
                <Form.Label>{t("regnumber")}</Form.Label>
                <Form.Control
                  type="text"
                  name="regnumber"
                  value={formData.regnumber}
                  onChange={handleChange}
                  required
                  isInvalid={!!validationErrors.regnumber}
                  className={`${
                    tableType ? `modal-${tableType}-field-regnumber` : ""
                  }`}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.regnumber
                    ? t(validationErrors.regnumber)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* Basic Identifiers */}
            {isBaseProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="baseid">
                  <Form.Label>{t("baseid")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="baseid"
                    value={formData.baseid}
                    onChange={handleChange}
                    required
                    isInvalid={!!validationErrors.baseid}
                    className={`${
                      tableType ? `modal-${tableType}-field-baseid` : ""
                    }`}
                  >
                    {unassignedBases.length === 0 ? (
                      <option value="" disabled>
                        {t("no_unassigned_bases")}
                      </option>
                    ) : (
                      <>
                        <option value="">{t("choose")}</option>
                        {unassignedBases.map((base) => (
                          <option key={base._id} value={base.baseid}>
                            {base.baseid}
                          </option>
                        ))}
                      </>
                    )}


                    
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.baseid
                      ? t(validationErrors.baseid)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}

            {!isBaseProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="probeid">
                  <Form.Label>{t("probeid")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="probeid"
                    value={formData.probeid}
                    onChange={handleChange}
                    required
                    isInvalid={!!validationErrors.probeid}
                    className={`${
                      tableType ? `modal-${tableType}-field-probeid` : ""
                    }`}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.probeid
                      ? t(validationErrors.probeid)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
            {/* Probe Details */}
            {!isBaseProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="probelength">
                  <Form.Label>{t("probelength")}</Form.Label>
                  <Form.Control
                    type="number"
                    name="probelength"
                    value={formData.probelength}
                    readOnly
                    className={`${
                      tableType ? `modal-${tableType}-field-probelength` : ""
                    } ${classes.autofilled}`}
                  />
                </Form.Group>
              </Col>
            )}
            {!isBaseProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="probesensnum">
                  <Form.Label>{t("probesensnum")}</Form.Label>
                  <Form.Control
                    type="number"
                    name="probesensnum"
                    value={formData.probesensnum}
                    readOnly
                    className={`${
                      tableType ? `modal-${tableType}-field-probesensnum` : ""
                    } ${classes.autofilled}`}
                  />
                </Form.Group>
              </Col>
            )}
            {/* Base Device Details */}
            {/* {!isProbeProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="imeei">
                  <Form.Label>{t("imeei")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="imeei"
                    value={formData.imeei}
                    onChange={handleChange}
                    required
                    readOnly
                    isInvalid={!!validationErrors.imeei}
                    className={`${
                      tableType ? `modal-${tableType}-field-imeei` : ""
                    }`}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.imeei
                      ? t(validationErrors.imeei)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}

            {!isProbeProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="sim">
                  <Form.Label>{t("sim")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="sim"
                    value={formData.sim}
                    onChange={handleChange}
                    required
                    readOnly
                    isInvalid={!!validationErrors.sim}
                    className={`${
                      tableType ? `modal-${tableType}-field-sim` : ""
                    }`}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.sim
                      ? t(validationErrors.sim)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
            {!isProbeProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="sim_id">
                  <Form.Label>{t("sim_id")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="sim_id"
                    value={formData.sim_id}
                    onChange={handleChange}
                    required
                    isInvalid={!!validationErrors.sim_id}
                    className={`${
                      tableType ? `modal-${tableType}-field-sim_id` : ""
                    }`}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.sim_id
                      ? t(validationErrors.sim_id)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )} */}

            {/* Hidden fields for SIM values (still needed for form submission) */}
            {!isProbeProduct() && (
              <>
                <input
                  type="hidden"
                  name="imeei"
                  value={formData.imeei || ""}
                />
                <input type="hidden" name="sim" value={formData.sim || ""} />
                <input
                  type="hidden"
                  name="sim_id"
                  value={formData.sim_id || ""}
                />
              </>
            )}
            {/* Status and Other Fields */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="status">
                <Form.Label>{t("status")}</Form.Label>
                <Form.Control
                  as="select"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  isInvalid={!!validationErrors.status}
                  className={`${
                    tableType ? `modal-${tableType}-field-status` : ""
                  }`}
                >
                  {Object.entries(PRODUCT_STATUS).map(([key, value]) => (
                    <option key={key} value={key}>
                      {t(value)}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.status
                    ? t(validationErrors.status)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="probe_type">
                <Form.Label>{t("probe_type")}</Form.Label>
                <Form.Control
                  type="text"
                  name="probe_type"
                  value={formData.probe_type}
                  readOnly
                  required
                  className={`${
                    tableType ? `modal-${tableType}-field-probe_type` : ""
                  } ${classes.autofilled}`}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="companies_id">
                <Form.Label>{t("company")}</Form.Label>
                <Form.Control
                  as="select"
                  name="companies_id"
                  value={formData.companies_id}
                  onChange={handleChange}
                  required
                  isInvalid={!!validationErrors.companies_id}
                  className={`${
                    tableType ? `modal-${tableType}-field-companies_id` : ""
                  }`}
                >
                  <option value="">{t("choose")}</option>
                  {companies.map((company) => (
                    <option key={company.companyid} value={company.companyid}>
                      {company.companyname}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.companies_id
                    ? t(validationErrors.companies_id)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12} className={classes.formGroup}>
              <Form.Group controlId="comments">
                <Form.Label>{t("comments")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.comments}
                  className={`${
                    tableType ? `modal-${tableType}-field-comments` : ""
                  }`}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.comments
                    ? t(validationErrors.comments)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {t("save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditProductModal;
